import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/lib/toast';
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, BookOpen, Layers, ChevronRight, Archive, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from 'primereact/dialog';
import { AutoComplete } from 'primereact/autocomplete';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-success/10 text-success-text',
  DRAFT: 'bg-warning/10 text-warning-text',
  ARCHIVED: 'bg-muted text-muted-foreground',
  DEPRECATED: 'bg-destructive/10 text-destructive-text',
};

type IngredientRow = {
  rawMaterialId: string;
  uom: string;
  quantity: string;
  standardCost: string;
  wastagePercent: string;
  groupLabel: string;
  _productName: string; // display label for the autocomplete input
};

/** Product row from `GET /products` (fields this page reads). */
interface RecipeProduct {
  id: string;
  name: string;
  sku?: string | null;
  productType?: string;
  costPrice?: number | string | null;
  unit?: string | null;
}

/** Envelope from `GET /products`. */
interface ProductsResponse {
  data: RecipeProduct[];
}

/** Recipe card row from `GET /recipes`. */
interface RecipeSummary {
  id: string;
  name: string;
  status: string;
  version?: number | string;
  yieldQuantity?: number | string;
  yieldUOM?: string;
  costPerUnit?: number | string | null;
  finishedProduct?: { name: string } | null;
  items?: unknown[];
}

/** Paginated envelope from `GET /recipes`. */
interface RecipesResponse {
  data: RecipeSummary[];
  total?: number;
  totalPages?: number;
}

/** Body for `POST /recipes`. */
interface CreateRecipeBody {
  name: string;
  description?: string;
  finishedProductId: string;
  yieldQuantity: number;
  yieldUOM: string;
  status: string;
  items: Array<{
    rawMaterialId: string;
    uom: string;
    quantity: number;
    standardCost: number;
    wastagePercent: number;
    groupLabel?: string;
  }>;
}

const EMPTY_INGREDIENT: IngredientRow = {
  rawMaterialId: '', uom: 'pcs', quantity: '1', standardCost: '0', wastagePercent: '0', groupLabel: '', _productName: '',
};

/** Self-contained product autocomplete — each row has its own dropdown state */
function ProductAutocomplete({
  value,
  displayName,
  productTypes,
  onSelect,
  placeholder = 'Type to search…',
}: {
  value: string;
  displayName: string;
  /** POS-I259: comma-separated product types to search (undefined = all products). */
  productTypes?: string;
  onSelect: (product: RecipeProduct) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState(displayName);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // POS-I259: search the server (no fixed product cap), debounced, instead of filtering one preloaded page.
  const [debounced, setDebounced] = useState(displayName);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const { data: found, isFetching } = useQuery({
    queryKey: ['recipe-product-search', productTypes ?? '', debounced],
    enabled: open,
    queryFn: async () => {
      const params: Record<string, string | number> = { limit: 20, page: 1, sortBy: 'name', sortOrder: 'asc' };
      if (debounced) params.search = debounced;
      if (productTypes) params.productType = productTypes;
      const res = await api.get<ProductsResponse>('/products', { params });
      return res.data?.data ?? [];
    },
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });
  const suggestions: RecipeProduct[] = found ?? [];
  const pending = isFetching || query.trim() !== debounced;

  // Sync display when parent resets
  if (!value && query && !open) setQuery('');

  return (
    <div ref={ref} className="relative w-full">
      <Input
        className="h-7 w-full rounded px-1.5 py-1 text-xs"
        value={query}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && suggestions.length === 0 && (
        <div className="absolute z-[9999] top-full inset-x-0 mt-0.5 bg-popover border border-border rounded-md shadow-lg px-2 py-1.5 text-xs text-muted-foreground">
          {pending ? t('searching') : t('productPickerEmpty')}
        </div>
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-[9999] top-full inset-x-0 mt-0.5 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((p) => (
            <button
              key={p.id}
              type="button"
              className="w-full text-start px-2 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
              onMouseDown={() => {
                onSelect(p);
                setQuery(p.name);
                setOpen(false);
              }}
            >
              <span className="flex-1 truncate">{p.name}</span>
              {p.sku && <span className="text-muted-foreground shrink-0">{p.sku}</span>}
              {p.productType === 'RAW_MATERIAL' && (
                <span className="shrink-0 text-[10px] px-1 py-0.5 rounded bg-success/10 text-success-text">Raw</span>
              )}
              {p.productType === 'SEMI_FINISHED' && (
                <span className="shrink-0 text-[10px] px-1 py-0.5 rounded bg-info/10 text-info-text">Semi</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RecipesPage() {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', finishedProductId: '', yieldQuantity: '1', yieldUOM: 'pcs', status: 'ACTIVE',
  });
  const [selectedFinishedProduct, setSelectedFinishedProduct] = useState<RecipeProduct | string | null>(null);
  const [finishedSuggestions, setFinishedSuggestions] = useState<RecipeProduct[]>([]);
  // POS-I259: PrimeReact opens its panel only when there are suggestions, so the empty state is shown under the field
  const [finishedEmpty, setFinishedEmpty] = useState(false);
  const [ingredients, setIngredients] = useState<IngredientRow[]>([{ ...EMPTY_INGREDIENT }]);

  const qc = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('recipes:create') || hasPermission('recipes:*') || hasPermission('*');

  const { data, isLoading } = useQuery<RecipesResponse>({
    queryKey: ['recipes', page, search],
    queryFn: () => api.get('/recipes', { params: { page, limit: 20, search: search || undefined } }).then(r => r.data),
    staleTime: 30_000,
  });

  // POS-I259: pickers search the server; this only learns whether any product is tagged raw material / semi-finished
  const { data: taggedProbe } = useQuery<{ total?: number }>({
    queryKey: ['recipe-tagged-probe'],
    queryFn: () => api.get('/products', { params: { limit: 1, productType: 'RAW_MATERIAL,SEMI_FINISHED' } }).then(r => r.data),
    staleTime: 120_000,
  });
  const taggedCount = taggedProbe?.total ?? 0;
  const ingredientTypes = taggedCount > 0 ? 'RAW_MATERIAL,SEMI_FINISHED' : undefined;

  const searchFinished = async (e: { query: string }) => {
    try {
      const res = await api.get<ProductsResponse>('/products', { params: { search: e.query.trim() || undefined, limit: 20, page: 1, sortBy: 'name', sortOrder: 'asc' } });
      const rows = res.data?.data ?? [];
      setFinishedSuggestions(rows);
      setFinishedEmpty(rows.length === 0);
    } catch {
      setFinishedSuggestions([]);
      setFinishedEmpty(false);
    }
  };

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/recipes/${id}`, { status: 'ARCHIVED' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recipes'] }); toast.success('Recipe archived'); },
    onError: () => toast.error('Failed to archive recipe'),
  });

  const createMutation = useMutation<{ id: string }, unknown, CreateRecipeBody>({
    mutationFn: (body) => api.post('/recipes', body).then(r => r.data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['recipes'] });
      toast.success('Recipe created');
      setDialogOpen(false);
      navigate(`/manufacturing/recipes/${created.id}`);
    },
    onError: (err) => notify.error(err, 'Failed to create recipe'),
  });

  const handleSubmit = () => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error('Recipe name must be at least 2 characters'); return; }
    if (!form.finishedProductId) { toast.error('Select a finished product'); return; }
    if (ingredients.some(i => !i.rawMaterialId)) { toast.error('All ingredients must have a product selected'); return; }
    if (ingredients.some(i => Number(i.quantity) <= 0)) { toast.error('All ingredient quantities must be > 0'); return; }

    createMutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      finishedProductId: form.finishedProductId,
      yieldQuantity: Number(form.yieldQuantity),
      yieldUOM: form.yieldUOM,
      status: form.status,
      items: ingredients.map(i => ({
        rawMaterialId: i.rawMaterialId,
        uom: i.uom,
        quantity: Number(i.quantity),
        standardCost: Number(i.standardCost),
        wastagePercent: Number(i.wastagePercent),
        groupLabel: i.groupLabel || undefined,
      })),
    });
  };

  const openNew = () => {
    setForm({ name: '', description: '', finishedProductId: '', yieldQuantity: '1', yieldUOM: 'pcs', status: 'ACTIVE' });
    setSelectedFinishedProduct(null);
    setFinishedEmpty(false);
    setIngredients([{ ...EMPTY_INGREDIENT }]);
    setDialogOpen(true);
  };

  const updateIngredient = (idx: number, field: keyof IngredientRow, val: string) => {
    setIngredients(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const selectIngredientProduct = (idx: number, product: RecipeProduct) => {
    setIngredients(prev => prev.map((r, i) => i === idx ? {
      ...r,
      rawMaterialId: product.id,
      standardCost: String(product.costPrice ?? 0),
      uom: product.unit ?? 'pcs',
      _productName: product.name,
    } : r));
  };

  const removeIngredient = (idx: number) => {
    if (ingredients.length === 1) return;
    setIngredients(prev => prev.filter((_, i) => i !== idx));
  };

  const recipes = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-warning to-warning/80 shadow-md">
              <BookOpen className="h-5 w-5 text-warning-foreground" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Recipes</h1>
          </div>
          <p className="text-sm text-muted-foreground">Bills of materials — define how raw materials convert to finished goods</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" /> New Recipe
          </Button>
        )}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search recipes..." className="ps-9" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Card key={i} className="h-32 animate-pulse bg-muted" />)}
        </div>
      ) : recipes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="font-medium text-muted-foreground">No recipes yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Create your first recipe to define how raw materials are converted into finished goods</p>
            {canWrite && <Button className="mt-4 gap-2" onClick={openNew}><Plus className="h-4 w-4" /> Create Recipe</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {recipes.map((recipe) => (
            <Card key={recipe.id} className="group hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/manufacturing/recipes/${recipe.id}`)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{recipe.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{recipe.finishedProduct?.name}</p>
                  </div>
                  <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap', STATUS_COLORS[recipe.status] ?? '')}>{recipe.status}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{recipe.items?.length ?? 0} ingredient{recipe.items?.length !== 1 ? 's' : ''}</span>
                  <span>v{recipe.version} · Yield: {recipe.yieldQuantity} {recipe.yieldUOM}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm font-semibold text-foreground">Cost/unit: {formatCurrency(Number(recipe.costPerUnit ?? 0))}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    {canWrite && recipe.status !== 'ARCHIVED' && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Archive" onClick={() => archiveMutation.mutate(recipe.id)}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate(`/manufacturing/recipes/${recipe.id}`)}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {total > 20 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data?.totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= (data?.totalPages ?? 1)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Create Recipe Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header="New Recipe"
        modal
        breakpoints={{ '960px': '90vw', '640px': '95vw' }}
        style={{ width: '65vw' }}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving…' : 'Create Recipe'}
            </Button>
          </div>
        }
      >
        <div className="space-y-5 py-2">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="mb-1 block">
                Recipe Name * <span className="text-xs text-muted-foreground font-normal">(min 2 characters)</span>
              </Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Chocolate Cake Batter"
                autoFocus
              />
            </div>
            <div className="col-span-2">
              <Label className="mb-1 block">Finished Product *</Label>
              <AutoComplete
                value={selectedFinishedProduct ?? undefined}
                suggestions={finishedSuggestions}
                completeMethod={searchFinished}
                emptyMessage={t('productPickerEmpty')}
                field="name"
                placeholder="Type to search products…"
                className="w-full"
                inputClassName="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                panelClassName="z-[9999]"
                itemTemplate={(item: RecipeProduct) => (
                  <div className="flex items-center gap-2 py-0.5">
                    <span className="text-sm">{item.name}</span>
                    <span className="text-xs text-muted-foreground ms-auto">SKU: {item.sku ?? '—'}</span>
                    {item.productType !== 'REGULAR' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-info/10 text-info-text">{item.productType}</span>
                    )}
                  </div>
                )}
                onChange={(e) => {
                  if (e.value && typeof e.value === 'object') {
                    setSelectedFinishedProduct(e.value);
                    setFinishedEmpty(false);
                    setForm(f => ({ ...f, finishedProductId: e.value.id }));
                  } else {
                    setSelectedFinishedProduct(e.value);
                    if (!e.value) setForm(f => ({ ...f, finishedProductId: '' }));
                  }
                }}
                forceSelection
                dropdown
              />
              {!form.finishedProductId && finishedEmpty && (
                <p className="text-xs text-muted-foreground mt-1" role="status">{t('productPickerEmpty')}</p>
              )}
              {!form.finishedProductId && (
                <p className="text-xs text-muted-foreground mt-1">Search by name or SKU. Any product can be a finished good.</p>
              )}
            </div>
            <div>
              <Label className="mb-1 block">Yield Quantity *</Label>
              <Input type="number" min="0.01" step="0.01" value={form.yieldQuantity} onChange={e => setForm(f => ({ ...f, yieldQuantity: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block">Yield Unit</Label>
              <Input value={form.yieldUOM} onChange={e => setForm(f => ({ ...f, yieldUOM: e.target.value }))} placeholder="pcs, kg, litres…" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1 block">Description (optional)</Label>
              <Textarea
                className="resize-none"
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Notes about this recipe…"
              />
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="font-semibold">Ingredients / Raw Materials *</Label>
                {taggedCount === 0 && (
                  <p className="text-xs text-warning-text mt-0.5">
                    Tip: Mark products as "Raw Material" in Products → Basics tab for easier filtering.
                  </p>
                )}
              </div>
              <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={() => setIngredients(prev => [...prev, { ...EMPTY_INGREDIENT }])}>
                <Plus className="h-3 w-3" /> Add Row
              </Button>
            </div>
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-2 py-2 text-start font-semibold text-muted-foreground uppercase tracking-wide">
                      Raw Material
                      {taggedCount > 0
                        ? <span className="ms-1 text-success-text font-normal normal-case">({taggedCount} tagged)</span>
                        : <span className="ms-1 text-warning-text font-normal normal-case text-[10px]">tag products as Raw Material in Products page</span>}
                    </th>
                    <th className="px-2 py-2 text-start font-semibold text-muted-foreground uppercase tracking-wide w-20">Qty</th>
                    <th className="px-2 py-2 text-start font-semibold text-muted-foreground uppercase tracking-wide w-16">UOM</th>
                    <th className="px-2 py-2 text-start font-semibold text-muted-foreground uppercase tracking-wide w-24">Cost/unit (PKR)</th>
                    <th className="px-2 py-2 text-start font-semibold text-muted-foreground uppercase tracking-wide w-16">Waste%</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients.map((row, idx) => (
                    <tr key={idx} className="border-t border-border">
                      <td className="px-2 py-1.5 min-w-[220px]">
                        <ProductAutocomplete
                          value={row.rawMaterialId}
                          displayName={row._productName}
                          productTypes={ingredientTypes}
                          onSelect={(p) => selectIngredientProduct(idx, p)}
                          placeholder="Search raw material…"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input className="h-7 text-xs" type="number" min="0.001" step="0.001" value={row.quantity} onChange={e => updateIngredient(idx, 'quantity', e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input className="h-7 text-xs" value={row.uom} onChange={e => updateIngredient(idx, 'uom', e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input className="h-7 text-xs" type="number" min="0" step="0.01" value={row.standardCost} onChange={e => updateIngredient(idx, 'standardCost', e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input className="h-7 text-xs" type="number" min="0" max="100" step="0.1" value={row.wastagePercent} onChange={e => updateIngredient(idx, 'wastagePercent', e.target.value)} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeIngredient(idx)} disabled={ingredients.length === 1}>
                          <X className="h-3 w-3 text-destructive-text" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Estimated cost preview */}
            {ingredients.some(i => i.rawMaterialId) && (
              <div className="mt-2 text-xs text-muted-foreground text-end">
                Estimated total cost: {formatCurrency(ingredients.reduce((sum, i) => {
                  const qty = Number(i.quantity) || 0;
                  const cost = Number(i.standardCost) || 0;
                  const waste = Number(i.wastagePercent) || 0;
                  return sum + cost * qty * (1 + waste / 100);
                }, 0))}
                {' '}→ Cost/unit: {formatCurrency(ingredients.reduce((sum, i) => {
                  const qty = Number(i.quantity) || 0;
                  const cost = Number(i.standardCost) || 0;
                  const waste = Number(i.wastagePercent) || 0;
                  return sum + cost * qty * (1 + waste / 100);
                }, 0) / (Number(form.yieldQuantity) || 1))}
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

